import { useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { FricampCar, CarSpotPayload } from '../src/plugins/carScreen';
import { hasPremiumAccess } from '../services/accessControl';
import { getDistance } from '../services/geometryUtils';
import { AppUser, CampingSpot, Coordinates } from '../types';

interface UseCarScreenOptions {
  user: AppUser | null;
  spots: CampingSpot[];
  userPosition: Coordinates | null;
  userHeading: number | null;
  activeRoute: {
    coordinates: [number, number][];
    distance: number;
    duration: number;
    destinationName: string;
  } | null;
}

/**
 * Pushes nearby spots, user position, and active route to Android Auto / CarPlay.
 * Only active for Premium users, admins, and testers on native platforms.
 */
export function useCarScreen({ user, spots, userPosition, userHeading, activeRoute }: UseCarScreenOptions) {
  const isNative = Capacitor.isNativePlatform();
  const hasAccess = hasPremiumAccess(user);
  const enabled = isNative && hasAccess;

  const lastPushRef = useRef<number>(0);

  // Push nearby spots when position or spots change
  useEffect(() => {
    if (!enabled || !userPosition) return;

    // Throttle to once per 5 seconds
    const now = Date.now();
    if (now - lastPushRef.current < 5000) return;
    lastPushRef.current = now;

    // Calculate distance and sort by proximity
    const spotsWithDistance = spots
      .filter(s => s.location?.latitude && s.location?.longitude && s.score !== undefined)
      .map(s => ({
        spot: s,
        distance: getDistance(
          userPosition.latitude, userPosition.longitude,
          s.location.latitude, s.location.longitude
        ),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 20);

    const payload: CarSpotPayload[] = spotsWithDistance.map(({ spot, distance }) => ({
      id: spot.id,
      name: spot.name || '',
      spotType: spot.spotType || 'confirmed',
      latitude: spot.location.latitude,
      longitude: spot.location.longitude,
      score: spot.score ?? 0,
      suitabilityColor: spot.suitabilityColor || '#78716c',
      distanceMeters: distance,
      isDriveable: spot.isDriveable !== false,
    }));

    FricampCar.updateNearbySpots({ spots: payload }).catch(() => {});
  }, [enabled, spots, userPosition]);

  // Push user position
  useEffect(() => {
    if (!enabled || !userPosition) return;
    FricampCar.updatePosition({
      latitude: userPosition.latitude,
      longitude: userPosition.longitude,
      heading: userHeading ?? 0,
    }).catch(() => {});
  }, [enabled, userPosition, userHeading]);

  // Push active route
  useEffect(() => {
    if (!enabled) return;
    if (activeRoute) {
      FricampCar.showRoute(activeRoute).catch(() => {});
    } else {
      FricampCar.clearRoute().catch(() => {});
    }
  }, [enabled, activeRoute]);
}
